import React, { useState, useEffect } from "react";
import Sidebar from "../components/sidebar";
import "../styles/feedback.css";
import { io } from "socket.io-client";

const questions = [
  "The system is easy to use.",
  "The analysis results are reliable.",
  "The recommendations are useful.",
  "The map helps me understand risks.",
  "I am satisfied with FireTrace.",
];

// ⚡ Connect to backend Socket.IO
const socket = io(
  "https://firetrace-capstone.onrender.com",
  {
    transports: ["websocket"],
  }
);
const Feedback = () => {
  const [answers, setAnswers] = useState({});
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [feedbackResults, setFeedbackResults] = useState([]);
  const [averages, setAverages] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [hasRatedToday, setHasRatedToday] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  // 🆕 Modals
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  // 🧭 Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(feedbackResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFeedback = feedbackResults.slice(startIndex, endIndex);

  // 🧠 Load user and feedbacks
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);

    // ✅ Check if user rated within 24h
    const lastFeedbackDate = localStorage.getItem("lastFeedbackDate");
    if (lastFeedbackDate) {
      const diffHrs =
        (new Date() - new Date(lastFeedbackDate)) / (1000 * 60 * 60);
      if (diffHrs < 24) setHasRatedToday(true);
    }

    if (storedUser?.type === "admin") {
      fetchFeedbackResults(storedUser);

      socket.on("newFeedback", (feedback) => {
        setFeedbackResults((prev) => {
          const updated = [feedback, ...prev];
          calculateAverages(updated);
          return updated;
        });
      });

      socket.on("deleteAllFeedback", () => {
        setFeedbackResults([]);
        setAverages({});
      });
    }

    return () => {
      socket.off("newFeedback");
      socket.off("deleteAllFeedback");
    };
  }, []);

  // 🧾 Fetch feedbacks
  const fetchFeedbackResults = async (storedUser) => {
    try {
      const res = await fetch(
        `https://firetrace-capstone.onrender.com/api/feedbacks/get?userId=${
          storedUser._id || storedUser.id
        }`
      );
      if (res.ok) {
        const data = await res.json();
        setFeedbackResults(data);
        calculateAverages(data);
      } else {
        setStatusMessage("❌ Failed to fetch feedbacks.");
      }
    } catch (err) {
      console.error("❌ Error fetching feedback results:", err);
      setStatusMessage("❌ Error fetching feedback results.");
    }
  };

  // 🧮 Calculate average ratings
  const calculateAverages = (feedbacks) => {
    const totals = {};
    const counts = {};
    feedbacks.forEach((fb) => {
      fb.answers.forEach(({ question, response }) => {
        totals[question] = (totals[question] || 0) + response;
        counts[question] = (counts[question] || 0) + 1;
      });
    });
    const avg = {};
    questions.forEach((q) => {
      avg[q] = counts[q] ? (totals[q] / counts[q]).toFixed(2) : 0;
    });
    setAverages(avg);
  };

  // ⭐ Handle rating input
  const handleAnswer = (q, rating) => setAnswers({ ...answers, [q]: rating });

  // 💬 Handle submit
  const handleSubmit = async () => {
    if (hasRatedToday) {
      setShowLimitModal(true);
      return;
    }

    if (!user?._id && !user?.id)
      return setStatusMessage("⚠️ Please log in first.");

    if (Object.keys(answers).length < questions.length)
      return setStatusMessage("⚠️ Please answer all questions.");

    const userId = user._id || user.id;
    const payload = {
      userId,
      answers: Object.keys(answers).map((q) => ({
        question: q,
        response: Number(answers[q]),
      })),
      message,
    };

    try {
      const res = await fetch("https://firetrace-capstone.onrender.com/api/feedbacks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        setStatusMessage("❌ Failed to send feedback.");
        return;
      }

      // ✅ Show success modal
      setShowFeedbackModal(true);
      localStorage.setItem("lastFeedbackDate", new Date().toISOString());
      setHasRatedToday(true);
      setAnswers({});
      setMessage("");
    } catch (err) {
      console.error("❌ Backend error:", err);
      setStatusMessage("❌ A server error occurred.");
    }
  };

  // 🗑️ Delete all feedbacks
  const handleDeleteAllConfirm = async () => {
    if (confirmText.toLowerCase() !== "delete") {
      setStatusMessage("❌ Please type 'delete' to confirm.");
      return;
    }

    try {
      const res = await fetch("https://firetrace-capstone.onrender.com/api/feedbacks/deleteAll", {
        method: "DELETE",
      });
      if (res.ok) {
        setStatusMessage("🗑️ All feedback entries deleted successfully!");
        setFeedbackResults([]);
        setAverages({});
        setShowDeleteModal(false);
        setConfirmText("");
      } else {
        setStatusMessage("❌ Failed to delete feedbacks.");
      }
    } catch (err) {
      console.error("❌ Error deleting all feedbacks:", err);
      setStatusMessage("❌ Server error while deleting feedbacks.");
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main
        className={
          user?.type === "admin"
            ? "admin-feedback-container"
            : "user-feedback-container"
        }
      >
        {user?.type === "admin" ? (
          feedbackResults.length === 0 ? (
            <p className="no-feedback-text">No feedback submitted yet.</p>
          ) : (
            <>
              <div className="table-header1">
                <h2>Feedback Overview</h2>
                <button
                  className="delete-all-btn"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete All
                </button>
              </div>

              <div className="average-summary">
                {questions.map((q, i) => (
                  <div key={i} className="average-box">
                    <p className="avg-question">{q}</p>
                    <p className="avg-value">
                      {averages[q]}{" "}
                      <span className="avg-stars">
                        {"★".repeat(Math.round(averages[q])) +
                          "☆".repeat(5 - Math.round(averages[q]))}
                      </span>
                    </p>
                  </div>
                ))}
              </div>

              <div className="table-wrapper1">
                <table className="feedback-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      {questions.map((q, i) => (
                        <th key={i}>{q}</th>
                      ))}
                      <th>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFeedback.map((fb, idx) => (
                      <tr key={idx}>
                        <td>{fb.userId?.name || "Anonymous"}</td>
                        {questions.map((q, i) => {
                          const answerObj = fb.answers.find(
                            (a) => a.question === q
                          );
                          return (
                            <td key={i}>
                              {answerObj?.response
                                ? "★".repeat(answerObj.response) +
                                  "☆".repeat(5 - answerObj.response)
                                : "-"}
                            </td>
                          );
                        })}
                        <td>{fb.message || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    &lt;
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, totalPages)
                      )
                    }
                    disabled={currentPage === totalPages}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )
        ) : (
          <div className="user-feedback-card">
            <h2>Please rate us</h2>
            {questions.map((q, idx) => (
              <div key={idx} className="feedback-question">
                <p>{q}</p>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`star ${answers[q] >= star ? "filled" : ""}`}
                      onClick={() => handleAnswer(q, star)}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            ))}
            <textarea
              placeholder="Do you have any thoughts you’d like to share? (Optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <button type="button" onClick={handleSubmit}>
              Submit Feedback
            </button>
            {statusMessage && <p className="status-message">{statusMessage}</p>}
          </div>
        )}
      </main>

      {/* 🗑️ Delete Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Deletion</h3>
            <p>
              This action will permanently delete <b>all feedback entries</b>.
              <br />
              Type <strong>delete</strong> to confirm.
            </p>
            <input
              type="text"
              placeholder="Type 'delete' here"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
            <div className="modal-actions">
              <button
                className="cancel-btn1"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button className="confirm-btn1" onClick={handleDeleteAllConfirm}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Success Feedback Modal */}
      {showFeedbackModal && (
        <div className="feedbackmodal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div
            className="feedbackmodal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Thank you!</h3>
            <p>Your feedback has been submitted successfully.</p>
            <button onClick={() => setShowFeedbackModal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* ⚠️ Limit Modal */}
      {showLimitModal && (
        <div className="feedbackmodal-overlay" onClick={() => setShowLimitModal(false)}>
          <div
            className="feedbackmodal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Limit Reached</h3>
            <p>You can only submit feedback once every 24 hours.</p>
            <button onClick={() => setShowLimitModal(false)}>Okay</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
